import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { safeBlobGet } from '../utils/fetchPdfBlob';
import { useAuth } from '../contexts/AuthContext';
import { AppShell } from '../components/AppShell';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Crown, Clock, ArrowLeft, Warning, X, Eye, CaretRight, CheckCircle,
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { PlansSection, BenefitsCard, InvoicesList } from '../components/subscription/SubscriptionWidgets';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function getSubCardStyle(status) {
  if (status === 'active' || status === 'trialing') return 'border-emerald-300 bg-emerald-50/50';
  if (status === 'grace_period') return 'border-amber-300 bg-amber-50/50';
  if (status === 'cancelled') return 'border-gray-300 bg-gray-50/50';
  return 'border-red-300 bg-red-50/50';
}

function getSubIconBg(status) {
  if (status === 'active') return 'bg-emerald-100';
  if (status === 'trialing') return 'bg-blue-100';
  if (status === 'grace_period') return 'bg-amber-100';
  return 'bg-red-100';
}

function getSubLabel(status) {
  if (status === 'active') return 'Actif';
  if (status === 'trialing') return 'Essai gratuit';
  if (status === 'grace_period') return 'Période de grace';
  if (status === 'cancelled') return 'Annule';
  return 'Expire';
}

function SubStatusIcon({ status }) {
  if (status === 'active') return <Crown size={24} className="text-emerald-600" weight="fill" />;
  if (status === 'trialing') return <Clock size={24} className="text-blue-600" />;
  if (status === 'grace_period') return <Warning size={24} className="text-amber-600" />;
  return <X size={24} className="text-red-600" />;
}

export function SubscriptionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [subscription, setSub] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const isAdmin = user?.role === 'admin';
  const sessionId = useMemo(() => searchParams.get('session_id'), [searchParams]);

  const loadData = useCallback(async () => {
    try {
      const [plansRes, subRes, invRes] = await Promise.all([
        axios.get(`${API}/subscriptions/plans`, { withCredentials: true }),
        axios.get(`${API}/subscriptions/my`, { withCredentials: true }),
        axios.get(`${API}/subscriptions/invoices`, { withCredentials: true }),
      ]);
      setPlans(plansRes.data || []);
      setSub(subRes.data);
      setInvoices(invRes.data || []);
    } catch (_err) { toast.error('Erreur chargement abonnement'); }
    finally { setLoading(false); }
  }, [setPlans, setSub, setInvoices, setLoading]);

  const checkPaymentStatus = useCallback(async (sid) => {
    try {
      const res = await axios.get(`${API}/subscriptions/checkout/status/${sid}`, { withCredentials: true });
      if (res.data.status === 'paid') {
        toast.success('Paiement confirme ! Votre abonnement est actif.');
        loadData();
        navigate('/subscription', { replace: true });
      }
    } catch (_err) { toast.error('Erreur verification paiement'); }
  }, [loadData, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [sessionId, checkPaymentStatus]);

  const [pendingCheckout, setPendingCheckout] = useState(null);
  const [cgvDoc, setCgvDoc] = useState(null);

  // Charge la CGV courante au montage
  useEffect(() => {
    axios.get(`${API}/legal/current`).then(r => {
      const cgv = (r.data.documents || []).find(d => d.doc_type === 'cgv');
      if (cgv) setCgvDoc(cgv);
    }).catch(() => {});
  }, []);

  const handleCheckout = (planId) => {
    // Ouvre un modal CGV avant de rediriger vers Stripe
    setPendingCheckout(planId);
  };

  const proceedCheckoutAfterCGV = async () => {
    const planId = pendingCheckout;
    if (!planId) return;
    setCheckingOut(planId);
    try {
      // Enregistre la preuve d'acceptation CGV avant le paiement
      if (cgvDoc) {
        await axios.post(`${API}/legal/accept`, {
          doc_id: cgvDoc.id, source: 'subscription',
          checkbox_label: `J'ai lu et j'accepte les CGV v${cgvDoc.version}`,
        }, { withCredentials: true }).catch(() => {});
      }
      const res = await axios.post(`${API}/subscriptions/checkout`, {
        plan_id: planId,
        origin_url: window.location.origin,
      }, { withCredentials: true });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du paiement');
    }
    finally { setCheckingOut(null); setPendingCheckout(null); }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API}/subscriptions/cancel`, {}, { withCredentials: true });
      toast.success('Abonnement annule');
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setCancelling(false); }
  };

  const downloadInvoice = async (invoiceId, invoiceNum) => {
    try {
      const res = await safeBlobGet(`${API}/subscriptions/invoices/${invoiceId}/pdf`);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `Facture_${invoiceNum}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (_err) { toast.error(_err.message || 'Erreur téléchargement'); }
  };

  const hasActiveSub = subscription?.has_subscription;
  const subStatus = subscription?.status;

  if (loading) return (
    <AppShell>
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" />
      </div>
    </AppShell>
  );

  return (
    <AppShell>
      <div className="space-y-6 max-w-4xl mx-auto" data-testid="subscription-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-10 w-10 md:hidden"
            onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft size={22} />
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#111827]" data-testid="subscription-title">
              Gagnez du temps sur vos chantiers dès aujourd'hui
            </h1>
            <p className="text-sm text-[#4B5563]">Sans engagement — Mise en place en 2 minutes</p>
          </div>
        </div>

        {/* Current subscription status */}
        {subscription && subStatus !== 'none' && subStatus !== 'admin' && (
          <Card className={`border-2 ${getSubCardStyle(subStatus)}`} data-testid="current-sub-card">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getSubIconBg(subStatus)}`}>
                    <SubStatusIcon status={subStatus} />
                  </div>
                  <div>
                    <h3 className="font-bold text-base">
                      {subscription.plan?.name || 'Abonnement'} — {getSubLabel(subStatus)}
                    </h3>
                    {subscription.message && (
                      <p className="text-sm text-[#4B5563] mt-0.5">{subscription.message}</p>
                    )}
                    {subscription.end_date && (
                      <p className="text-xs text-[#6B7280] mt-1">
                        Valide jusqu'au {new Date(subscription.end_date).toLocaleDateString('fr-FR')}
                      </p>
                    )}
                  </div>
                </div>
                {(subStatus === 'active' || subStatus === 'trialing') && (
                  <Button variant="outline" size="sm" onClick={handleCancel} disabled={cancelling}
                    className="text-red-600 border-red-200 hover:bg-red-50 h-10 flex-shrink-0"
                    data-testid="cancel-sub-btn">
                    {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Annuler'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Admin preview banner */}
        {isAdmin && (
          <div className="flex items-center gap-2 bg-slate-800 text-white rounded-lg p-3 text-sm" data-testid="admin-notice">
            <Eye size={18} weight="bold" />
            <span><strong>Mode aperçu administrateur</strong> — Vous visualisez la page telle que vos utilisateurs la voient.</span>
          </div>
        )}

        {((!hasActiveSub || subStatus === 'expired' || subStatus === 'grace_period') || isAdmin) && (
          <PlansSection plans={plans} isAdmin={isAdmin} checkingOut={checkingOut} onCheckout={handleCheckout} />
        )}

        <BenefitsCard />
        <InvoicesList invoices={invoices} downloadInvoice={downloadInvoice} />
      </div>
      {pendingCheckout && (
        <CGVAcceptDialog
          plan={plans.find(p => p.id === pendingCheckout)}
          cgvDoc={cgvDoc}
          onCancel={() => setPendingCheckout(null)}
          onConfirm={proceedCheckoutAfterCGV}
          loading={!!checkingOut}
        />
      )}
    </AppShell>
  );
}

function CGVAcceptDialog({ plan, cgvDoc, onCancel, onConfirm, loading }) {
  const [accepted, setAccepted] = React.useState(false);
  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onCancel()}
      data-testid="cgv-dialog">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4">
        <h3 className="text-lg font-bold text-[#111827]">Confirmation avant paiement</h3>
        <p className="text-sm text-[#4B5563]">
          Vous êtes sur le point de souscrire au plan <strong>{plan?.name}</strong>
          {plan?.price != null && <> — <strong>{plan.price} €/mois</strong></>}.
        </p>
        <label className="flex items-start gap-2 p-3 border border-gray-200 rounded-lg bg-gray-50 cursor-pointer">
          <input type="checkbox" checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-gray-300 text-[#0055FF]"
            data-testid="cgv-accept-checkbox" />
          <span className="text-xs text-[#4B5563] leading-relaxed">
            J&apos;ai lu et j&apos;accepte les{' '}
            <a href="/cgv" target="_blank" rel="noreferrer" className="text-[#0055FF] font-medium hover:underline">
              Conditions Générales de Vente
            </a>
            {cgvDoc && <span className="text-gray-400"> (v{cgvDoc.version})</span>}.
            Je reconnais avoir été informé du prix, des modalités de facturation et de la période d&apos;essai éventuelle.
          </span>
        </label>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onCancel} disabled={loading}>Annuler</Button>
          <Button onClick={onConfirm} disabled={!accepted || loading}
            className="bg-[#0055FF] hover:bg-blue-700 text-white" data-testid="cgv-confirm-btn">
            {loading ? 'Redirection…' : 'Continuer vers le paiement'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Success callback page
export function SubscriptionSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');

  const checkStatus = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res = await axios.get(`${API}/subscriptions/checkout/status/${sessionId}`, { withCredentials: true });
      setStatus(res.data.status === 'paid' ? 'success' : 'pending');
    } catch (err) {
      setStatus('error');
    }
  }, [sessionId, setStatus]);

  useEffect(() => {
    if (sessionId) {
      checkStatus();
    }
  }, [sessionId, checkStatus]);

  return (
    <AppShell>
      <div className="flex flex-col items-center justify-center h-[60vh] text-center px-6" data-testid="subscription-success">
        {status === 'checking' && <Loader2 className="h-10 w-10 animate-spin text-[#FF6B00] mb-4" />}
        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <CheckCircle size={36} className="text-emerald-600" weight="fill" />
            </div>
            <h2 className="text-2xl font-bold text-[#111827]">Paiement confirme !</h2>
            <p className="text-[#4B5563] mt-2 max-w-md">
              Votre abonnement CHANTIX est maintenant actif. Profitez de toutes les fonctionnalites.
            </p>
          </>
        )}
        {status === 'pending' && (
          <>
            <Clock size={36} className="text-amber-500 mb-4" />
            <h2 className="text-xl font-bold text-[#111827]">Paiement en cours de traitement</h2>
            <p className="text-[#4B5563] mt-2">Votre paiement est en cours. Vous serez notifie des la confirmation.</p>
          </>
        )}
        {status === 'error' && (
          <>
            <Warning size={36} className="text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-[#111827]">Erreur de verification</h2>
            <p className="text-[#4B5563] mt-2">Impossible de vérifiér le paiement. Contactez le support.</p>
          </>
        )}
        <Button onClick={() => navigate('/subscription')} className="mt-6 bg-[#FF6B00] hover:bg-orange-700 h-12 px-6"
          data-testid="go-subscription-btn">
          <CaretRight size={18} className="mr-2" /> Voir mon abonnement
        </Button>
      </div>
    </AppShell>
  );
}
